/**
 * Panneau d'actions rapides pour la page Consultation Notes.
 *
 * 4 actions :
 *   1. Reporter examen → D1 + D2  (notes_devoirs/reporter-examen-vers-devoirs-trace)
 *   2. Note identique sur classe + matière (notes_examens/note-collective ou notes_devoirs/note_collective)
 *   3. Note collective sur cases vides uniquement (note-collective avec ecraser_existants=false)
 *   4. Déclarer absent les cases vides (notes_examens/attribuer-zero-absents OU notes_devoirs/marquer-absents-vides)
 *
 * Toutes les opérations sont tracées (batch_id annulable via /annuler-batch/).
 */
import React, { useState } from 'react';
import { Zap, X, ArrowRightLeft, Award, FileQuestion, UserX, Loader, AlertTriangle, CheckCircle2, Eraser, ArrowLeftRight } from 'lucide-react';
import apiClient from '../../config/api.config';

type Cible = 'examen' | 'devoirs' | 'les_deux';
type Action = 'reporter_examen' | 'reporter_devoir' | 'note_identique' | 'note_vides' | 'absents_vides' | 'vider_notes';
type SourceDevoir = 'note1' | 'note2' | 'moyenne';

interface Props {
  matiereId: number;
  classe: string;
  semestre: 'S1' | 'S2';
  anneeAcademique: string;
  onSuccess: () => void;
}

const ActionsRapidesNotesPanel: React.FC<Props> = ({ matiereId, classe, semestre, anneeAcademique, onSuccess }) => {
  const [activeModal, setActiveModal] = useState<Action | null>(null);
  const [valeur, setValeur] = useState('');
  const [cible, setCible] = useState<Cible>('devoirs');
  const [champ, setChamp] = useState<'note1' | 'note2' | 'les_deux'>('les_deux');
  const [commentaire, setCommentaire] = useState('');
  const [reportMode, setReportMode] = useState<'manquants_seulement' | 'tous'>('manquants_seulement');
  const [sourceDevoir, setSourceDevoir] = useState<SourceDevoir>('moyenne');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const closeModal = () => {
    setActiveModal(null);
    setValeur('');
    setCible('devoirs');
    setChamp('les_deux');
    setCommentaire('');
    setReportMode('manquants_seulement');
    setSourceDevoir('moyenne');
  };

  // Récupère les étudiants de la classe (utilisé pour reporter examen → devoirs)
  const fetchEtudiantsClasse = async (): Promise<number[]> => {
    const res = await apiClient.get('/etudiants/', { params: { classe, actif: true, page_size: 500 } });
    const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
    return list.map((e: any) => e.id);
  };

  // ── 1. Reporter examen → D1 + D2 ──────────────────────────────────────────
  const handleReporterExamen = async () => {
    const modeLabel = reportMode === 'tous'
      ? 'Mode : écrase TOUTES les cases (D1 + D2 même si déjà saisies)'
      : 'Mode : seulement les cases vides (ne touche pas aux devoirs déjà saisis)';
    if (!window.confirm(
      `Reporter la note d'examen (SO/SR) sur D1 et D2 pour la classe ${classe} ?\n\n${modeLabel}`
    )) return;
    setLoading(true);
    setMessage(null);
    try {
      const etudiants_ids = await fetchEtudiantsClasse();
      const res = await apiClient.post('/notes-devoirs/reporter-examen-vers-devoirs-trace/', {
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre,
        etudiants_ids,
        mode: reportMode,
        commentaire: commentaire || `Report depuis NotesList — ${classe}`,
      });
      const nbCrees = res.data.nb_crees ?? 0;
      const nbModifies = res.data.nb_modifies ?? 0;
      const nbSansExamen = res.data.nb_sans_note_examen ?? 0;
      const nbDejaComplet = res.data.nb_devoir_deja_complet ?? 0;
      const nbEtuIntr = res.data.nb_etudiant_introuvable ?? 0;
      const total = nbCrees + nbModifies;

      if (total > 0) {
        const skipParts: string[] = [];
        if (nbDejaComplet > 0) skipParts.push(`${nbDejaComplet} déjà complets`);
        if (nbSansExamen > 0) skipParts.push(`${nbSansExamen} sans note d'examen`);
        const skipMsg = skipParts.length > 0 ? ` · ${skipParts.join(', ')} ignoré(s)` : '';
        setMessage({
          type: 'success',
          text: `${total} note(s) reportée(s) — ${nbCrees} créée(s), ${nbModifies} modifiée(s)${skipMsg} · batch ${res.data.batch_id?.slice(0, 8)}…`,
        });
        closeModal();
      } else {
        // Diagnostic clair selon la cause dominante
        const parts: string[] = [];
        if (nbDejaComplet > 0) {
          parts.push(`${nbDejaComplet} étudiant(s) ont déjà D1 et D2 saisis (mode "vides uniquement" actif — coche "Écraser" pour forcer)`);
        }
        if (nbSansExamen > 0) {
          parts.push(`${nbSansExamen} étudiant(s) sans note d'examen pour cette matière (vérifie que la matière correspond et que les notes sont en session ordinaire S1/S2)`);
        }
        if (nbEtuIntr > 0) {
          parts.push(`${nbEtuIntr} étudiant(s) introuvable(s) en base`);
        }
        setMessage({
          type: 'error',
          text: parts.length > 0
            ? `Aucune note reportée. ${parts.join(' · ')}`
            : `Aucune note reportée. Vérifie matière + classe + session.`,
        });
      }
      onSuccess();
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || 'Erreur lors du report' });
    } finally {
      setLoading(false);
    }
  };

  // ── 1bis. Reporter Devoir (D1, D2 ou Moyenne) → Examen ──────────────────
  const handleReporterDevoir = async () => {
    const sourceLabel = sourceDevoir === 'note1' ? 'D1' : sourceDevoir === 'note2' ? 'D2' : 'Moyenne devoirs';
    const modeLabel = reportMode === 'tous'
      ? 'Mode : écrase la note examen même si déjà saisie'
      : 'Mode : seulement si la case examen est vide';
    if (!window.confirm(
      `Reporter ${sourceLabel} sur la note examen (SO) pour la classe ${classe} ?\n\n${modeLabel}`
    )) return;
    setLoading(true);
    setMessage(null);
    try {
      const etudiants_ids = await fetchEtudiantsClasse();
      const res = await apiClient.post('/notes-examens/reporter-devoir-vers-examen-trace/', {
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre,
        etudiants_ids,
        source: sourceDevoir,
        mode: reportMode,
        commentaire: commentaire || `Report ${sourceLabel} → SO depuis NotesList — ${classe}`,
      });
      const nbCrees = res.data.nb_crees ?? 0;
      const nbModif = res.data.nb_modifies ?? 0;
      const nbSansDev = res.data.nb_sans_note_devoir ?? 0;
      const nbSourceVide = res.data.nb_source_vide ?? 0;
      const nbDejaSaisi = res.data.nb_examen_deja_saisi ?? 0;
      const total = nbCrees + nbModif;
      if (total > 0) {
        const skipParts: string[] = [];
        if (nbDejaSaisi > 0) skipParts.push(`${nbDejaSaisi} examen déjà saisi`);
        if (nbSansDev > 0) skipParts.push(`${nbSansDev} sans note devoir`);
        if (nbSourceVide > 0) skipParts.push(`${nbSourceVide} ${sourceLabel} vide`);
        const skipMsg = skipParts.length > 0 ? ` · ${skipParts.join(', ')} ignoré(s)` : '';
        setMessage({
          type: 'success',
          text: `${total} note(s) reportée(s) ${sourceLabel}→SO — ${nbCrees} créée(s), ${nbModif} modifiée(s)${skipMsg} · batch ${res.data.batch_id?.slice(0, 8)}…`,
        });
        closeModal();
      } else {
        const parts: string[] = [];
        if (nbDejaSaisi > 0) {
          parts.push(`${nbDejaSaisi} étudiant(s) ont déjà une note examen (mode "vides uniquement" actif — coche "Écraser" pour forcer)`);
        }
        if (nbSansDev > 0) {
          parts.push(`${nbSansDev} étudiant(s) sans note de devoir pour cette matière`);
        }
        if (nbSourceVide > 0) {
          parts.push(`${nbSourceVide} étudiant(s) ont un NoteDevoir mais ${sourceLabel} est vide`);
        }
        setMessage({
          type: 'error',
          text: parts.length > 0
            ? `Aucune note reportée. ${parts.join(' · ')}`
            : `Aucune note reportée. Vérifie matière + classe + session.`,
        });
      }
      onSuccess();
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || 'Erreur lors du report' });
    } finally {
      setLoading(false);
    }
  };

  // ── 2. Note identique sur tous (3. Note vides via ecraser_existants=false) ───
  const handleNoteIdentique = async (cassesVidesUniquement: boolean) => {
    const v = parseFloat(valeur);
    if (isNaN(v) || v < 0 || v > 20) {
      setMessage({ type: 'error', text: 'Valeur entre 0 et 20 requise' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const promesses: Promise<any>[] = [];
      const ecraser = !cassesVidesUniquement;

      if (cible === 'examen' || cible === 'les_deux') {
        promesses.push(apiClient.post('/notes-examens/note-collective/', {
          matiere_id: matiereId,
          classes: [classe],
          annee_academique: anneeAcademique,
          semestre,
          valeur: v,
          ecraser_existants: ecraser,
          commentaire: commentaire || `Action rapide — ${classe} — ${cassesVidesUniquement ? 'cases vides' : 'tous'}`,
        }));
      }
      if (cible === 'devoirs' || cible === 'les_deux') {
        promesses.push(apiClient.post('/notes-devoirs/note_collective/', {
          matiere_id: matiereId,
          classes: [classe],
          annee_academique: anneeAcademique,
          semestre,
          champ,
          valeur: v,
          ecraser_existants: ecraser,
          commentaire: commentaire || `Action rapide — ${classe} — ${cassesVidesUniquement ? 'cases vides' : 'tous'}`,
        }));
      }
      const results = await Promise.all(promesses);
      const total = results.reduce((acc, r) => acc + (r.data?.nb_modifies ?? 0) + (r.data?.nb_crees ?? 0) + (r.data?.modifies ?? 0) + (r.data?.crees ?? 0), 0);
      setMessage({ type: 'success', text: `${total} note(s) traitée(s) avec valeur ${v}` });
      closeModal();
      onSuccess();
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || 'Erreur lors de l\'attribution' });
    } finally {
      setLoading(false);
    }
  };

  // ── 4. Déclarer absents pour cases vides ─────────────────────────────────
  const handleAbsentsVides = async () => {
    if (!window.confirm(
      `Déclarer absents tous les étudiants avec des cases ${cible === 'examen' ? 'examen' : (cible === 'devoirs' ? 'devoir' : 'devoir/examen')} vides pour ${classe} ?\n\n` +
      `Les notes existantes ne sont PAS modifiées.`
    )) return;
    setLoading(true);
    setMessage(null);
    try {
      const promesses: Promise<any>[] = [];

      if (cible === 'examen' || cible === 'les_deux') {
        // Pour examen : récupérer étudiants sans note + appeler attribuer-zero-absents
        const etudiants_ids = await fetchEtudiantsClasse();
        promesses.push(apiClient.post('/notes-examens/attribuer-zero-absents/', {
          matiere_id: matiereId,
          etudiants_ids,
          annee_academique: anneeAcademique,
          semestre,
          commentaire: commentaire || `Cases vides — ${classe}`,
        }));
      }
      if (cible === 'devoirs' || cible === 'les_deux') {
        promesses.push(apiClient.post('/notes-devoirs/marquer-absents-vides/', {
          matiere_id: matiereId,
          classes: [classe],
          annee_academique: anneeAcademique,
          semestre,
          champ,
          commentaire: commentaire || `Cases vides — ${classe}`,
        }));
      }
      const results = await Promise.all(promesses);
      const total = results.reduce((acc, r) =>
        acc + (r.data?.nb_marques_d1 ?? 0) + (r.data?.nb_marques_d2 ?? 0) + (r.data?.nb_modifiees ?? 0) + (r.data?.modifies ?? 0), 0);
      setMessage({ type: 'success', text: `${total} étudiant(s) marqués absents` });
      closeModal();
      onSuccess();
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || 'Erreur' });
    } finally {
      setLoading(false);
    }
  };

  // ── 5. Vider les notes D1/D2 ──────────────────────────────────────────────
  const handleViderNotes = async () => {
    const champLabel = champ === 'les_deux' ? 'D1 et D2' : champ === 'note1' ? 'D1' : 'D2';
    if (!window.confirm(
      `⚠ ATTENTION — Action destructive\n\n` +
      `Vider toutes les notes ${champLabel} pour la classe ${classe} ?\n\n` +
      `Toutes les valeurs ${champLabel} actuellement saisies (et leurs marquages "absent") ` +
      `seront effacées et remises à blanc.\n\n` +
      `→ L'opération est tracée (batch_id) et peut être annulée via l'historique.`
    )) return;

    setLoading(true);
    setMessage(null);
    try {
      const res = await apiClient.post('/notes-devoirs/vider-notes/', {
        matiere_id: matiereId,
        classes: [classe],
        annee_academique: anneeAcademique,
        semestre,
        champ,
        commentaire: commentaire || `Vidage ${champLabel} — ${classe}`,
      });
      const nbModif = res.data.nb_modifies ?? 0;
      const nbD1 = res.data.nb_d1_vides ?? 0;
      const nbD2 = res.data.nb_d2_vides ?? 0;
      setMessage({
        type: nbModif > 0 ? 'success' : 'error',
        text: nbModif > 0
          ? `${nbModif} note(s) vidée(s) — D1: ${nbD1}, D2: ${nbD2} · batch ${res.data.batch_id?.slice(0, 8)}…`
          : `Aucune note à vider (toutes déjà vides ou aucune saisie pour ${classe}).`,
      });
      if (nbModif > 0) closeModal();
      onSuccess();
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || 'Erreur lors du vidage' });
    } finally {
      setLoading(false);
    }
  };

  if (!matiereId || !classe) return null;

  return (
    <>
      {/* Bouton Actions rapides + boutons individuels */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-2 flex items-center gap-2 flex-wrap">
        <Zap size={14} className="text-amber-500" />
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">Actions rapides :</span>
        <button onClick={() => setActiveModal('reporter_examen')}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200"
          title="Copier la note examen (SO/SR) sur D1 et D2">
          <ArrowRightLeft size={11} /> Examen → D1+D2
        </button>
        <button onClick={() => setActiveModal('reporter_devoir')}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded hover:bg-cyan-200"
          title="Copier une note de devoir (D1, D2 ou Moyenne) vers la note examen (SO)">
          <ArrowLeftRight size={11} /> Devoir → Examen
        </button>
        <button onClick={() => setActiveModal('note_identique')}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200">
          <Award size={11} /> Note identique
        </button>
        <button onClick={() => { setCible('devoirs'); setActiveModal('note_vides'); }}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded hover:bg-emerald-200"
          title="Attribuer une note identique aux cases D1/D2 vides uniquement">
          <FileQuestion size={11} /> Devoirs vides
        </button>
        <button onClick={() => { setCible('examen'); setActiveModal('note_vides'); }}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded hover:bg-cyan-200"
          title="Attribuer une note identique aux cases d'examen vides uniquement">
          <FileQuestion size={11} /> Examens vides
        </button>
        <button onClick={() => setActiveModal('absents_vides')}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded hover:bg-orange-200">
          <UserX size={11} /> Déclarer absents (cases vides)
        </button>
        <button onClick={() => { setChamp('les_deux'); setActiveModal('vider_notes'); }}
          title="Effacer toutes les notes D1/D2 saisies pour cette classe + matière"
          className="flex items-center gap-1 px-2 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200">
          <Eraser size={11} /> Vider notes D1/D2
        </button>
        {message && (
          <span className={`text-xs ml-auto flex items-center gap-1 ${message.type === 'success' ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
            {message.type === 'success' ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
            {message.text}
          </span>
        )}
      </div>

      {/* Modal commune */}
      {activeModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                {activeModal === 'reporter_examen' && <><ArrowRightLeft size={16} className="text-blue-600" />Reporter examen → D1 + D2</>}
                {activeModal === 'reporter_devoir' && <><ArrowLeftRight size={16} className="text-cyan-600" />Reporter devoir → Examen (SO)</>}
                {activeModal === 'note_identique' && <><Award size={16} className="text-purple-600" />Note identique</>}
                {activeModal === 'note_vides' && <><FileQuestion size={16} className="text-emerald-600" />
                  Note collective — cases vides ({cible === 'devoirs' ? 'devoirs' : cible === 'examen' ? 'examens' : 'devoirs + examens'})
                </>}
                {activeModal === 'absents_vides' && <><UserX size={16} className="text-orange-600" />Déclarer absents (cases vides)</>}
                {activeModal === 'vider_notes' && <><Eraser size={16} className="text-red-600" />Vider toutes les notes ({champ === 'les_deux' ? 'D1 + D2' : champ === 'note1' ? 'D1' : 'D2'})</>}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>

            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              Classe <strong>{classe}</strong> · Matière <strong>#{matiereId}</strong> · {semestre} · {anneeAcademique}
            </p>

            {/* Mode (vides uniquement / écraser tout) — pour reporter_examen */}
            {activeModal === 'reporter_examen' && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Mode</label>
                <div className="space-y-1.5">
                  <label className="flex items-start gap-2 cursor-pointer p-2 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <input
                      type="radio"
                      name="reportMode"
                      checked={reportMode === 'manquants_seulement'}
                      onChange={() => setReportMode('manquants_seulement')}
                      className="mt-0.5"
                    />
                    <div className="text-xs">
                      <div className="font-medium text-gray-800 dark:text-gray-200">Cases vides uniquement</div>
                      <div className="text-gray-500 dark:text-gray-400">
                        Ne touche pas aux notes D1/D2 déjà saisies. Ignoré si l'étudiant a déjà ses 2 devoirs.
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer p-2 border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded">
                    <input
                      type="radio"
                      name="reportMode"
                      checked={reportMode === 'tous'}
                      onChange={() => setReportMode('tous')}
                      className="mt-0.5"
                    />
                    <div className="text-xs">
                      <div className="font-medium text-amber-800 dark:text-amber-300">Écraser TOUTES les cases</div>
                      <div className="text-amber-700 dark:text-amber-400">
                        Remplace D1 et D2 même si déjà saisis (ancienne valeur tracée — annulable via batch_id).
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Source + mode pour reporter_devoir → examen */}
            {activeModal === 'reporter_devoir' && (
              <>
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Source à copier vers la note examen (SO)
                  </label>
                  <select
                    value={sourceDevoir}
                    onChange={(e) => setSourceDevoir(e.target.value as SourceDevoir)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                  >
                    <option value="moyenne">Moyenne devoirs (D1+D2)/2</option>
                    <option value="note1">Devoir 1 uniquement</option>
                    <option value="note2">Devoir 2 uniquement</option>
                  </select>
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    Si la source est vide pour un étudiant, il est ignoré.
                  </p>
                </div>
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Mode</label>
                  <div className="space-y-1.5">
                    <label className="flex items-start gap-2 cursor-pointer p-2 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <input
                        type="radio"
                        name="reportModeDev"
                        checked={reportMode === 'manquants_seulement'}
                        onChange={() => setReportMode('manquants_seulement')}
                        className="mt-0.5"
                      />
                      <div className="text-xs">
                        <div className="font-medium text-gray-800 dark:text-gray-200">Cases examen vides uniquement</div>
                        <div className="text-gray-500 dark:text-gray-400">
                          Ignoré si l'étudiant a déjà une note examen (SO).
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer p-2 border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded">
                      <input
                        type="radio"
                        name="reportModeDev"
                        checked={reportMode === 'tous'}
                        onChange={() => setReportMode('tous')}
                        className="mt-0.5"
                      />
                      <div className="text-xs">
                        <div className="font-medium text-amber-800 dark:text-amber-300">Écraser la note examen</div>
                        <div className="text-amber-700 dark:text-amber-400">
                          Remplace SO même si déjà saisie (ancienne valeur tracée — annulable).
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </>
            )}

            {/* Cible (devoirs/examen/les_deux) — pour actions 2/3/4 */}
            {(activeModal === 'note_identique' || activeModal === 'note_vides' || activeModal === 'absents_vides') && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cible</label>
                <select value={cible} onChange={(e) => setCible(e.target.value as Cible)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
                  <option value="devoirs">Devoirs (D1, D2)</option>
                  <option value="examen">Examen (SO)</option>
                  <option value="les_deux">Les deux</option>
                </select>
              </div>
            )}

            {/* Champ devoirs (D1/D2/les_deux) si cible inclut devoirs */}
            {(cible === 'devoirs' || cible === 'les_deux') && (activeModal === 'note_identique' || activeModal === 'note_vides' || activeModal === 'absents_vides') && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Devoir(s) ciblé(s)</label>
                <select value={champ} onChange={(e) => setChamp(e.target.value as any)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
                  <option value="les_deux">D1 et D2</option>
                  <option value="note1">D1 uniquement</option>
                  <option value="note2">D2 uniquement</option>
                </select>
              </div>
            )}

            {/* Champ pour vider_notes (D1/D2/les_deux) */}
            {activeModal === 'vider_notes' && (
              <>
                <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded text-xs text-red-800 dark:text-red-300 flex items-start gap-2">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <div>
                    <strong>Action destructive</strong> — toutes les valeurs saisies dans
                    le(s) champ(s) ci-dessous seront effacées (mises à blanc) pour les
                    étudiants de la classe <strong>{classe}</strong>.
                    Tracé avec batch_id, donc annulable via l'historique des opérations.
                  </div>
                </div>
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Devoir(s) à vider</label>
                  <select value={champ} onChange={(e) => setChamp(e.target.value as any)}
                    className="w-full px-2 py-1.5 text-sm border border-red-300 dark:border-red-700 rounded bg-white dark:bg-gray-700">
                    <option value="les_deux">D1 et D2 (tout vider)</option>
                    <option value="note1">D1 uniquement</option>
                    <option value="note2">D2 uniquement</option>
                  </select>
                </div>
              </>
            )}

            {/* Valeur — pour actions 2 et 3 */}
            {(activeModal === 'note_identique' || activeModal === 'note_vides') && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Note à attribuer (0 - 20)
                </label>
                <input
                  type="number" min="0" max="20" step="0.25"
                  value={valeur}
                  onChange={(e) => setValeur(e.target.value)}
                  placeholder="Ex: 10"
                  autoFocus
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                />
              </div>
            )}

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Commentaire (motif)</label>
              <input type="text" value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
                placeholder="ex: Décision conseil de classe…"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700" />
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded p-2 mb-3 text-xs text-amber-800 dark:text-amber-300">
              <AlertTriangle size={12} className="inline mr-1" />
              Toutes les modifications sont tracées (batch_id) et peuvent être annulées via l'historique.
            </div>

            {message && (
              <div className={`p-2 rounded text-xs mb-2 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {message.text}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={closeModal}
                className="px-4 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-sm hover:bg-gray-300">
                Annuler
              </button>
              <button
                onClick={() => {
                  if (activeModal === 'reporter_examen') handleReporterExamen();
                  else if (activeModal === 'reporter_devoir') handleReporterDevoir();
                  else if (activeModal === 'note_identique') handleNoteIdentique(false);
                  else if (activeModal === 'note_vides') handleNoteIdentique(true);
                  else if (activeModal === 'absents_vides') handleAbsentsVides();
                  else if (activeModal === 'vider_notes') handleViderNotes();
                }}
                disabled={loading}
                className={`flex items-center gap-1 px-4 py-1.5 text-white rounded text-sm disabled:opacity-50 ${
                  activeModal === 'vider_notes'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? <Loader size={14} className="animate-spin" /> : (activeModal === 'vider_notes' ? <Eraser size={14} /> : <Zap size={14} />)}
                {activeModal === 'vider_notes' ? 'Vider les notes' : 'Exécuter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActionsRapidesNotesPanel;
